import Badge from '@orn/shared-web/components/Badge/Badge';
import { cx } from '@orn/shared-web/utils/cx';
import {
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
} from 'react-aria-components';

import { displayName, formatRelationship } from '../../../lib/dependents/helpers';

import DependentCardBody from './DependentCardBody';

import type { IDependentCardBodyProps } from './DependentCardBody';

const PersonTab = ({ person }: { person: IDependentCardBodyProps }) => {
  const name =
    displayName({ firstName: person.state.firstName, preferredName: person.state.preferredName }) || 'Loved one';
  return (
    <AriaTab
      id={person.dependentId}
      className={({ isSelected, isFocusVisible }) =>
        cx(
          'outline-focus-ring relative flex min-w-40 flex-1 cursor-pointer flex-col items-start gap-1 rounded-t-3xl px-5 py-3',
          isSelected && 'bg-primary border-secondary border-x border-t',
          isFocusVisible && 'outline-2 -outline-offset-2'
        )
      }
    >
      <span className="text-primary text-md max-w-full truncate font-semibold">{name}</span>
      {person.state.relationship && (
        <Badge type="pill-color" size="sm" color="brand">
          {formatRelationship({
            relationship: person.state.relationship,
            isStep: person.state.isStep,
            isInLaw: person.state.isInLaw,
          })}
        </Badge>
      )}
    </AriaTab>
  );
};
PersonTab.displayName = 'PersonTab';

export interface ITabbedDependentsCardProps {
  people: IDependentCardBodyProps[];
}

/**
 * The mobile/tablet dependents editor (Figma node `5388:26535`): one settings
 * card with a person tab per dependent in its shaded header — a "file folder"
 * tab showing the name and relationship badge — and the selected dependent's
 * editable sections as the card body. The desktop layout renders the same
 * bodies as an accordion stack instead (see `DependentSettingsFormBase`).
 */
const TabbedDependentsCard = ({ people }: ITabbedDependentsCardProps) => (
  <AriaTabs
    defaultSelectedKey={people[0]?.dependentId}
    className="bg-primary border-secondary shadow-card overflow-hidden rounded-2xl border [overflow-anchor:none]"
  >
    <div className="bg-secondary flex flex-col gap-3 pt-4">
      <p className="text-primary px-5 text-lg font-semibold">Contacts Information</p>
      <div className="relative">
        {/* Painted before the (relative) tab list so the selected tab's white
            background covers the line, merging the tab into the panel below. */}
        <div aria-hidden className="border-secondary absolute inset-x-0 bottom-0 border-b" />
        <AriaTabList aria-label="Loved ones" className="scrollbar-hide relative flex overflow-x-auto">
          {people.map((person) => (
            <PersonTab key={person.dependentId} person={person} />
          ))}
        </AriaTabList>
      </div>
    </div>
    {people.map((person) => (
      <AriaTabPanel
        key={person.dependentId}
        id={person.dependentId}
        className="outline-focus-ring flex flex-col gap-8 px-5 py-6"
      >
        <DependentCardBody {...person} />
      </AriaTabPanel>
    ))}
  </AriaTabs>
);

TabbedDependentsCard.displayName = 'TabbedDependentsCard';

export default TabbedDependentsCard;
